import { useState } from "react";
import { useActiveScenario, useScenarioRun } from "@/hooks/world";
import { fmtValue, pct } from "@/lib/format";
import { fmtByDate } from "@/lib/months";
import { headline, heroDate } from "@/lib/results";
import { cn } from "@/lib/utils";
import { useStore } from "@/model/store";
import type { Link, MetricNode } from "@/model/types";

/**
 * The link's effect on its target while it is being tuned (PRD §10.3 at its smallest):
 * "Model 28% → 31% by end of 2028". "Before" is the model when the editor opened. The API recomputes
 * after each change is saved (plan P3), so a number still being dragged shows as pending.
 */
export function EffectPreview({ link, draft }: { link: Link; draft: Link | null }) {
	const target = useStore((s) => s.world.nodes[link.to]);
	const { scenario } = useActiveScenario();
	const run = useScenarioRun(scenario?.id);
	const date = target ? heroDate(target) : null;
	const now = run.data && target && date ? headline(target, run.data, date) : null;
	// The first settled number is "before" (a state update during render, the React way to derive from a prop).
	const [b, setBefore] = useState<number | null>(null);
	if (b === null && now !== null && !run.pending) setBefore(now);
	if (!target || !date) return null;
	const fmt = (v: number | null) => (v === null ? "…" : target.kind === "event" ? pct(v) : fmtValue(v, (target as MetricNode).unit));
	const busy = run.pending || !!draft;
	const showAfter = b !== null && (busy || fmt(now) !== fmt(b));

	return (
		<p className="text-xs text-muted-foreground" aria-live="polite">
			<span className="text-foreground">{target.title}</span>: Model <span className="font-medium text-[var(--model)] tabular-nums">{fmt(b)}</span>
			{showAfter && (
				<>
					{" → "}
					<span className={cn("font-semibold text-[var(--model)] tabular-nums", busy && "opacity-50")}>{draft && !run.pending ? "…" : fmt(now)}</span>
				</>
			)}{" "}
			by {fmtByDate(date)}
		</p>
	);
}
